/**
 * MarkdownComposer — minimal textarea-based markdown composer.
 *
 * Phase 0 ships the textarea. A Tiptap-backed RichTextEditor will land
 * in a polish PR. The server (caitapi/services/community._sanitize_
 * markdown) runs nh3 on every write path, so the security boundary is
 * the same regardless of which editor produces the bytes.
 *
 * Help text inlines the most common markdown so new users don't bounce
 * off "wait what's `**bold**`?". Mirrors GitHub's "supports markdown"
 * affordance pattern.
 */
import * as React from "react";
import { useId } from "react";

export interface MarkdownComposerProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  /** Min character count (mirrors the API's Pydantic validator). */
  minLength?: number;
  /** Max character count (mirrors the API's Pydantic validator). */
  maxLength?: number;
  /** Disables the textarea (e.g. while submitting). */
  disabled?: boolean;
  /** Optional rows attribute (default 6). */
  rows?: number;
  /** Show character count under the textarea. */
  showCount?: boolean;
}

export function MarkdownComposer({
  value,
  onChange,
  label,
  placeholder,
  minLength,
  maxLength,
  disabled,
  rows = 6,
  showCount = true,
}: MarkdownComposerProps): React.JSX.Element {
  const id = useId();
  const helpId = `${id}-help`;
  const countId = `${id}-count`;

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        minLength={minLength}
        maxLength={maxLength}
        aria-describedby={`${helpId} ${countId}`}
        className="mt-1 block w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
      />
      <p id={helpId} className="mt-1 text-xs text-muted-foreground">
        Supports markdown: <code>**bold**</code>, <code>*italic*</code>,{" "}
        <code>[link](https://example.com)</code>,{" "}
        <code>- list item</code>, <code>&gt; quote</code>.
      </p>
      {showCount && maxLength && (
        <p
          id={countId}
          className={`mt-0.5 text-xs ${
            value.length > maxLength
              ? "text-red-700"
              : "text-muted-foreground"
          }`}
          aria-live="polite"
        >
          {value.length} / {maxLength}
        </p>
      )}
    </div>
  );
}
